import { useState, type ReactNode } from 'react';
import { useAuthStore } from '../../stores/auth';
import { useNotificationsStore, type AppNotification } from '../../stores/notifications';
import HelpTab from './HelpTab';
import AdminPanel from '../admin/AdminPanel';
import SettingsPanel from '../settings/SettingsPanel';
import NotificationList from './NotificationList';
import RoleChip from '../ui/RoleChip';
import { Menu, Settings, Users, HelpCircle, Info, LogOut } from '../../lib/icons';

interface TopBarProps {
  onNotificationNavigate?: (notif: AppNotification) => void;
}

const menuCardStyle: React.CSSProperties = {
  position: 'absolute', top: 'calc(100% + 6px)', right: 0,
  background: 'var(--surface-raised)',
  border: '1px solid var(--shell-border)',
  borderRadius: 8,
  boxShadow: '0 12px 32px rgba(0,0,0,.55)',
  zIndex: 100, overflow: 'hidden',
};

export default function TopBar({ onNotificationNavigate }: TopBarProps) {
  const user        = useAuthStore((s) => s.user);
  const logout      = useAuthStore((s) => s.logout);
  const unreadCount = useNotificationsStore((s) => s.unreadCount);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showHamburger, setShowHamburger] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const initials = user?.avatarInitials ?? user?.avatar_initials
    ?? user?.name?.split(' ').map((p) => p[0]).join('').toUpperCase().slice(0, 2)
    ?? '?';

  function closeAll() {
    setShowUserMenu(false);
    setShowHamburger(false);
  }

  const hamburgerItems = [
    { icon: <Settings size={15} strokeWidth={1.8} />, label: 'Settings', action: () => { closeAll(); setShowSettings(true); } },
    ...(user?.role === 'admin'
      ? [{ icon: <Users size={15} strokeWidth={1.8} />, label: 'User Management', action: () => { closeAll(); setShowAdmin(true); } }]
      : []),
    { icon: <HelpCircle size={15} strokeWidth={1.8} />, label: 'Help', action: () => { closeAll(); setShowHelp(true); } },
    { icon: <Info size={15} strokeWidth={1.8} />, label: 'About', action: () => { closeAll(); setShowAbout(true); } },
  ];

  return (
    <>
      <div style={{
        height: 56, background: 'var(--vs0)', borderBottom: '1px solid var(--shell-border)',
        display: 'flex', alignItems: 'center', padding: '0 20px', gap: 16, flexShrink: 0,
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginRight: 8 }}>
          <img src="/logo.svg" style={{ height: 22, width: 'auto' }} />
          <span style={{ fontSize: '0.95rem', fontWeight: 800, letterSpacing: '-0.01em' }}>
            <span style={{ color: 'var(--shell-text)' }}>Relay</span><span style={{ color: 'var(--brand-accent)' }}>Through</span>
          </span>
        </div>

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Right side */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {/* Avatar + name/role — opens the combined account & notifications menu */}
          <div style={{ position: 'relative' }}>
            <div
              onClick={() => { setShowHamburger(false); setShowUserMenu((v) => !v); }}
              style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', userSelect: 'none' }}
            >
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <div style={{
                  width: 30, height: 30, borderRadius: '50%', background: 'var(--v1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.74rem', fontWeight: 700, color: 'var(--text-on-accent)',
                }}>
                  {initials}
                </div>
                {unreadCount > 0 && (
                  <span style={{
                    position: 'absolute', top: -1, right: -1,
                    width: 10, height: 10, borderRadius: '50%',
                    background: 'var(--red)', border: '2px solid var(--vs0)',
                  }} />
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
                <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--shell-muted)' }}>{user?.name}</span>
                <RoleChip role={user?.role} />
              </div>
            </div>

            {showUserMenu && (
              <div style={{ ...menuCardStyle, width: 320 }}>
                <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--shell-border)' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--shell-text)' }}>{user?.name}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--shell-xs)', marginTop: 2 }}>{user?.email}</div>
                </div>

                <NotificationList
                  onNavigate={onNotificationNavigate ?? (() => {})}
                  onItemClick={() => setShowUserMenu(false)}
                />

                <MenuRow
                  icon={<LogOut size={15} strokeWidth={1.8} />}
                  label="Sign Out"
                  danger
                  last
                  onClick={() => { closeAll(); logout(); }}
                />
              </div>
            )}
          </div>

          {/* Hamburger menu */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => { setShowUserMenu(false); setShowHamburger((v) => !v); }}
              title="Menu"
              style={{
                background: showHamburger ? 'var(--accent-tint)' : 'transparent',
                border: 'none', borderRadius: 6, padding: '6px 8px',
                cursor: 'pointer', color: 'var(--shell-xs)', display: 'flex', alignItems: 'center',
                transition: 'background .12s',
              }}
            >
              <Menu size={17} strokeWidth={1.8} />
            </button>
            {showHamburger && (
              <div style={{ ...menuCardStyle, width: 200 }}>
                {hamburgerItems.map((item, i) => (
                  <MenuRow
                    key={item.label}
                    icon={item.icon}
                    label={item.label}
                    onClick={item.action}
                    last={i === hamburgerItems.length - 1}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Help modal */}
      {showHelp && (
        <Modal title="Help & Reference" onClose={() => setShowHelp(false)}>
          <HelpTab />
        </Modal>
      )}

      {/* Admin panel */}
      {showAdmin && <AdminPanel onClose={() => setShowAdmin(false)} />}

      {/* Settings panel */}
      {showSettings && <SettingsPanel onClose={() => setShowSettings(false)} />}

      {/* About modal */}
      {showAbout && (
        <Modal title="About RelayThrough" onClose={() => setShowAbout(false)}>
          <div style={{ padding: '32px 24px', textAlign: 'center', color: 'var(--shell-muted)', fontSize: '0.85rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 8 }}>
              <img src="/logo.svg" style={{ height: 36, width: 'auto' }} />
              <span style={{ fontSize: '1.5rem', fontWeight: 800, letterSpacing: '-0.02em' }}>
                <span style={{ color: 'var(--shell-text)' }}>Relay</span><span style={{ color: 'var(--brand-accent)' }}>Through</span>
              </span>
            </div>
            <div style={{ marginBottom: 4 }}>Client Communication Platform</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--shell-xs)', marginTop: 16 }}>Version 1.0.0</div>
            <div style={{ marginTop: 20, display: 'flex', gap: 16, justifyContent: 'center', fontSize: '0.75rem' }}>
              <a href="https://relaythrough.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--v2)', textDecoration: 'none' }}>Privacy Policy</a>
              <a href="https://relaythrough.com/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--v2)', textDecoration: 'none' }}>Terms of Service</a>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// Shared row for the avatar/hamburger dropdown cards — matches the sidebar's
// floating section-picker card (rounded container, hover rows, no emoji icons).
function MenuRow({ icon, label, onClick, danger, last }: {
  icon: ReactNode; label: string; onClick: () => void; danger?: boolean; last?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 9, width: '100%',
        padding: '10px 12px', background: 'none', border: 'none',
        borderBottom: last ? 'none' : '1px solid var(--shell-border)',
        color: danger ? 'var(--red)' : 'var(--shell-text)',
        fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', textAlign: 'left',
        transition: 'background .12s',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--hover)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
    >
      {icon}
      {label}
    </button>
  );
}

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--vs1)', border: '1px solid var(--shell-border)', borderRadius: 6,
        width: '85vw', maxWidth: 860, maxHeight: '85vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '0 24px 64px rgba(0,0,0,.5)',
      }}>
        {/* Modal header */}
        <div style={{
          padding: '14px 20px', borderBottom: '1px solid var(--shell-border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
        }}>
          <span style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--shell-text)' }}>{title}</span>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', color: 'var(--shell-xs)',
              fontSize: '1.1rem', cursor: 'pointer', lineHeight: 1, padding: '2px 6px',
              borderRadius: 3, transition: 'color .12s',
            }}
          >✕</button>
        </div>
        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {children}
        </div>
      </div>
    </div>
  );
}
